import type { PropsWithChildren } from "react";
import classNames from "classnames";
import type { RegisterOptions } from "react-hook-form";
import { get, useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Typography } from "@circle-react-uikit/Typography";
import { CheckoutFormInput } from "./CheckoutFormInput";

export interface CheckoutFormFieldProps {
  autoFocus?: boolean;
  checkoutFormClassName?: string;
  className?: string;
  disabled?: boolean;
  name: string;
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  options?: RegisterOptions;
  placeholder?: string;
  type: "text" | "password";
  wrapperClassName?: string;
}

export const CheckoutFormField = ({
  autoFocus = false,
  className,
  name,
  placeholder,
  options = {},
  type,
  children,
  disabled,
  wrapperClassName,
  checkoutFormClassName,
  onBlur,
  onKeyDown,
}: PropsWithChildren<CheckoutFormFieldProps>) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const error = get(errors, name);
  const registration = register(name, options);

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    await registration.onBlur(event);
    if (onBlur) onBlur(event);
  };

  return (
    <div className={classNames(className, "flex flex-col gap-y-1")}>
      <div className={classNames("flex flex-row gap-x-2.5", wrapperClassName)}>
        <CheckoutFormInput
          autoFocus={autoFocus}
          type={type}
          error={!!error}
          placeholder={placeholder}
          {...registration}
          onBlur={handleBlur}
          disabled={disabled}
          className={checkoutFormClassName}
          onKeyDown={onKeyDown}
        />
        {children}
      </div>

      {error && name !== "password" && (
        <Typography.LabelXs weight="normal" color="text-feedback-error">
          {t(`paywall_checkout.form.errors.${name}_${String(error.type)}`)}
        </Typography.LabelXs>
      )}
    </div>
  );
};
